/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import './audio-pulse.css';
import { useEffect, useRef } from 'react';
import c from 'classnames';

const lineCount = 3;

export type AudioPulseProps = {
  active: boolean;
  volume: number;
  hover?: boolean;
};

export default function AudioPulse({ active, volume, hover }: AudioPulseProps) {
  const lines = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    let timeout: number | null = null;
    const update = () => {
      lines.current.forEach((line, i) => {
        if (line) {
          const height = Math.max(4, Math.min(24, 4 + volume * (i === 1 ? 400 : 60)));
          line.style.height = `${height}px`;
        }
      });
      timeout = window.setTimeout(update, 100);
    };

    update();
    return () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, [volume]);

  return (
    <div className={c('audioPulse', { active, hover })} style={{ opacity: active ? 1 : 0.5 }}>
      {Array(lineCount)
        .fill(null)
        .map((_, i) => (
          <div
            key={i}
            ref={(el) => (lines.current[i] = el)}
            style={{
              animationDelay: `${i * 133}ms`,
              height: '16px',
            }}
          />
        ))}
    </div>
  );
}
